import { Link, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { FiSearch, FiArrowRight, FiChevronDown, FiX } from "react-icons/fi";
// import Logo from "../assets/images/logo.png";
import gsap from "gsap";

// Move firmsList outside the component since it's static data
const firmsList = [
  {
    name: "Gensler",
    description:
      "Global architecture, design, and planning firm with 50+ locations worldwide",
    path: "/firms/gensler",
  },
  {
    name: "Arcadis",
    description:
      "Leading global design, engineering, and management consulting company.",
    path: "/firms/arcadis",
  },
  {
    name: "HDR (Henningson, Durham & Richardson)",
    description:
      "International architecture, engineering, and consulting firm.",
    path: "/firms/hdr",
  },
  {
    name: "Nikken Sekkei",
    description:
      "Japan's largest architecture firm, known for innovative sustainable urban development",
    path: "/firms/nikken-sekkei",
  },
  {
    name: "AECOM",
    description:
      "Global infrastructure consulting firm, delivering professional services throughout the project lifecycle.",
    path: "/firms/aecom",
  },
  {
    name: "Perkins + Will",
    description:
      "Global architecture and design firm committed to sustainable, healthy, and equitable spaces",
    path: "/firms/perkins-will",
  },
  {
    name: "Skidmore, Owings & Merrill (SOM)",
    description:
      "Renowned architecture, interior design, engineering, and urban planning firm.",
    path: "/firms/som",
  },
  {
    name: "Foster + Partners",
    description:
      "Global studio for sustainable architecture, urbanism, and design, led by Norman Foster",
    path: "/firms/foster-partners",
  },
];

const Navbar = () => {
  const location = useLocation();
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [selectedResultIndex, setSelectedResultIndex] = useState(-1);
  const searchInputRef = useRef(null);
  const searchResultsRef = useRef(null);
  const megaMenuRef = useRef(null);
  const firmRefs = useRef([]);
  const mobileMenuRef = useRef(null);
  const mobileNavItemsRef = useRef([]);
  const mobileSearchRef = useRef(null);
  const mobileHeaderRef = useRef(null);
  const mobileSubMenuRefs = useRef([]);
  const navRef = useRef(null);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        activeDropdown &&
        navRef.current &&
        !navRef.current.contains(event.target)
      ) {
        setActiveDropdown(null);
      }

      // Close search results if clicked outside
      if (
        searchResultsRef.current &&
        !searchResultsRef.current.contains(event.target) &&
        !searchInputRef.current.contains(event.target)
      ) {
        setIsSearchFocused(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [activeDropdown]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isSearchFocused) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setSelectedResultIndex((prev) =>
            prev < searchResults.length - 1 ? prev + 1 : prev
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setSelectedResultIndex((prev) => (prev > 0 ? prev - 1 : -1));
          break;
        case "Enter":
          e.preventDefault();
          if (selectedResultIndex >= 0 && searchResults[selectedResultIndex]) {
            window.location.href = searchResults[selectedResultIndex].path;
          }
          break;
        case "Escape":
          setIsSearchFocused(false);
          break;
        default:
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isSearchFocused, selectedResultIndex, searchResults]);

  // Handle search
  useEffect(() => {
    if (searchQuery.length > 0) {
      const filteredResults = firmsList.filter(
        (firm) =>
          firm.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          firm.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setSearchResults(filteredResults);
      setSelectedResultIndex(-1);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery]);

  useEffect(() => {
    if (activeDropdown === "/firms" && megaMenuRef.current) {
      gsap.fromTo(
        megaMenuRef.current,
        {
          opacity: 0,
          y: -20,
        },
        {
          opacity: 1,
          y: 0,
          duration: 0.3,
          ease: "power2.out",
        }
      );

      gsap.fromTo(
        firmRefs.current,
        {
          opacity: 0,
          x: -20,
        },
        {
          opacity: 1,
          x: 0,
          duration: 0.4,
          stagger: 0.05,
          ease: "power2.out",
        }
      );
    }
  }, [activeDropdown]);

  useEffect(() => {
    if (isMobileMenuOpen && mobileMenuRef.current) {
      // Animate the container
      gsap.fromTo(
        mobileMenuRef.current,
        {
          opacity: 0,
        },
        {
          opacity: 1,
          duration: 0.3,
          ease: "power2.out",
        }
      );

      // Animate header elements
      gsap.fromTo(
        mobileHeaderRef.current,
        {
          opacity: 0,
          y: -20,
        },
        {
          opacity: 1,
          y: 0,
          duration: 0.4,
          ease: "power2.out",
        }
      );

      // Animate search bar
      gsap.fromTo(
        mobileSearchRef.current,
        {
          opacity: 0,
          y: -10,
        },
        {
          opacity: 1,
          y: 0,
          duration: 0.4,
          delay: 0.1,
          ease: "power2.out",
        }
      );

      // Animate nav items with stagger
      gsap.fromTo(
        mobileNavItemsRef.current,
        {
          opacity: 0,
          x: 40,
        },
        {
          opacity: 1,
          x: 0,
          duration: 0.4,
          stagger: 0.05,
          delay: 0.2,
          ease: "power2.out",
        }
      );
    }
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (activeDropdown === "/firms" && mobileSubMenuRefs.current.length > 0) {
      gsap.fromTo(
        mobileSubMenuRefs.current,
        {
          opacity: 0,
          x: -20,
        },
        {
          opacity: 1,
          x: 0,
          duration: 0.3,
          stagger: 0.05,
          ease: "power2.out",
        }
      );
    }
  }, [activeDropdown]);

  const handleFirmHover = (index, isEnter) => {
    gsap.to(firmRefs.current[index], {
      scale: isEnter ? 1.02 : 1,
      duration: 0.2,
      ease: "power1.out",
    });
  };

  const isActive = (path) => {
    if (path === "/" && location.pathname !== "/") return false;
    return location.pathname.startsWith(path);
  };

  const navLinks = [
    { path: "/expertise", label: "Expertise" },
    // { path: "/about", label: "About" },
    { path: "/blog", label: "Blog" },
    {
      path: "/firms",
      label: "Firms",
      isMegaMenu: true,
    },
  ];

  const handleSearchFocus = () => {
    setIsSearchFocused(true);
    if (searchQuery.length > 0) {
      const filteredResults = firmsList.filter(
        (firm) =>
          firm.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          firm.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setSearchResults(filteredResults);
    }
  };

  return (
    <nav className="bg-white sticky top-0 z-50" ref={navRef}>
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-16">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0">
              <div className="flex items-center gap-2">
                {/* <img
                  src={Logo}
                  alt="Wonderful Eight"
                  className="h-6"
                  style={{ transform: "translate(0px, 0px) !important" }}
                /> */}
                <p className="text-md md:text-lg lg:text-xl font-medium text-black max-w-3xl text-center">
                  Wonderful Eight
                </p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8">
              {navLinks.map((link) => (
                <div
                  key={link.path}
                  className="flex items-center"
                  onClick={() =>
                    link.isMegaMenu
                      ? setActiveDropdown(
                          activeDropdown === link.path ? null : link.path
                        )
                      : null
                  }
                >
                  {link.isMegaMenu ? (
                    <button
                      className={`text-lg font-medium transition-colors duration-200 flex items-center gap-1 ${
                        isActive(link.path)
                          ? "text-black"
                          : "text-gray-600 hover:text-black"
                      }`}
                    >
                      {link.label}
                      <FiChevronDown
                        className={`w-5 h-5 transition-transform duration-200 ${
                          activeDropdown === link.path ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                  ) : (
                    <Link
                      to={link.path}
                      className={`text-lg font-medium transition-colors duration-200 ${
                        isActive(link.path)
                          ? "text-black"
                          : "text-gray-600 hover:text-black"
                      }`}
                    >
                      {link.label}
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Desktop Search and Contact */}
          <div className="hidden lg:flex items-center space-x-4">
            <div className="relative">
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={handleSearchFocus}
                placeholder="Search firms..."
                className="pl-10 pr-4 py-2 bg-gray-100 rounded-none w-64 text-sm focus:outline-none"
              />
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />

              {/* Search Results Popup */}
              {isSearchFocused && searchQuery.length > 0 && (
                <div
                  ref={searchResultsRef}
                  className="absolute top-full left-0 right-0 mt-1 bg-white shadow-lg border border-gray-200 max-h-96 overflow-y-auto z-50"
                >
                  {searchResults.length > 0 ? (
                    searchResults.map((result, index) => (
                      <Link
                        key={result.path}
                        to={result.path}
                        className={`block p-4 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 ${
                          selectedResultIndex === index ? "bg-gray-50" : ""
                        }`}
                        onMouseEnter={() => setSelectedResultIndex(index)}
                        onClick={() => {
                          setIsSearchFocused(false);
                          setSearchQuery("");
                        }}
                      >
                        <h4 className="text-sm font-medium text-gray-900">
                          {result.name}
                        </h4>
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                          {result.description}
                        </p>
                      </Link>
                    ))
                  ) : (
                    <div className="p-4 text-center">
                      <p className="text-sm text-gray-500">
                        No firms found matching "{searchQuery}"
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        Try searching with a different term
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
            <Link
              to="/contact"
              className="flex items-center space-x-2 px-6 py-2 border-2 border-black rounded-none hover:bg-black hover:text-white transition-colors duration-200"
            >
              <span className="text-sm font-medium">Contact</span>
              <FiArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <FiX className="w-6 h-6" />
            ) : (
              <div className="space-y-2">
                <div className="w-8 h-0.5 bg-black"></div>
                <div className="w-8 h-0.5 bg-black"></div>
                <div className="w-8 h-0.5 bg-black"></div>
              </div>
            )}
          </button>
        </div>

        {/* Desktop Mega Menu */}
        {activeDropdown === "/firms" && !isMobileMenuOpen && (
          <div
            ref={megaMenuRef}
            className="absolute left-0 right-0 bg-white shadow-lg mt-1 hidden lg:block"
          >
            <div className="mx-auto px-4 sm:px-6 lg:px-20 py-6">
              <div className="grid grid-cols-2 gap-8">
                {firmsList.map((firm, index) => (
                  <Link
                    key={firm.path}
                    to={firm.path}
                    className="block transform"
                    ref={(el) => (firmRefs.current[index] = el)}
                    onMouseEnter={() => handleFirmHover(index, true)}
                    onMouseLeave={() => handleFirmHover(index, false)}
                    onClick={() => setActiveDropdown(null)}
                  >
                    <div className="border-l-2 border-blue-500 pl-4">
                      <h3 className="text-base font-medium text-gray-900">
                        {firm.name}
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        {firm.description}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div
            ref={mobileMenuRef}
            className="fixed inset-0 bg-white z-50 overflow-y-auto"
          >
            {/* Mobile Header */}
            <div
              ref={mobileHeaderRef}
              className="flex items-center justify-between px-4 py-3 border-b border-gray-100"
            >
              <Link to="/" className="flex-shrink-0">
                {/* <img src={Logo} alt="8Xarchitects" className="h-6" /> */}
                <p className="text-md md:text-lg lg:text-xl font-medium text-black max-w-3xl text-center">
                  Wonderful Eight
                </p>
              </Link>
              <div className="flex items-center gap-4">
                <Link
                  to="/contact"
                  className="flex items-center gap-1.5 border border-black px-3 py-1.5 text-sm"
                >
                  <span>Contact</span>
                  <FiArrowRight className="w-3.5 h-3.5" />
                </Link>
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="p-1.5"
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Mobile Search */}
            <div ref={mobileSearchRef} className="px-4 py-4">
              <div className="bg-gray-100">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Hinted search text"
                    className="w-full pl-4 pr-10 py-2.5 bg-transparent text-sm focus:outline-none"
                  />
                  <FiSearch className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-900 w-4 h-4" />
                </div>
              </div>
            </div>

            {/* Mobile Navigation */}
            <div className="px-4 flex flex-col">
              {navLinks.map((link, index) => (
                <div
                  key={link.path}
                  ref={(el) => (mobileNavItemsRef.current[index] = el)}
                >
                  {link.isMegaMenu ? (
                    <div>
                      <button
                        onClick={() => {
                          setActiveDropdown(
                            activeDropdown === link.path ? null : link.path
                          );
                          if (activeDropdown !== link.path) {
                            mobileSubMenuRefs.current = [];
                          }
                        }}
                        className="py-3 text-2xl font-medium w-full flex items-center"
                      >
                        {link.label}
                        <FiChevronDown
                          className={`w-6 h-6 transition-transform duration-200 ml-2 ${
                            activeDropdown === link.path ? "rotate-180" : ""
                          }`}
                        />
                      </button>
                      {activeDropdown === link.path && (
                        <div className="space-y-4 py-4 pl-6">
                          {firmsList.map((firm, firmIndex) => (
                            <Link
                              key={firm.path}
                              to={firm.path}
                              className="block"
                              ref={(el) =>
                                (mobileSubMenuRefs.current[firmIndex] = el)
                              }
                              onClick={() => setIsMobileMenuOpen(false)}
                            >
                              <h3 className="text-xl font-medium">
                                {firm.name}
                              </h3>
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      to={link.path}
                      className="block py-3 text-2xl font-medium"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {link.label}
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
